import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';

import { useReport } from '../context/ReportContext';
import { ReportComponentHelper } from '../helpers/ReportComponentHelper';
import type { IPeriodFilter } from '../types/period';
import type { IReportItem } from '../types/report';
import { PeriodControls } from './PeriodControls';

interface IOverrideDialogProps {
  item: IReportItem | null;
  onClose: () => void;
}

/** Lets one component follow its own period instead of the report-level filter. */
export const OverrideDialog = ({ item, onClose }: IOverrideDialogProps) => {
  const { dataset, booksClosedThrough, filter, setItems } = useReport();
  const [enabled, setEnabled] = useState(false);
  const [draft, setDraft] = useState<IPeriodFilter>(filter);

  useEffect(() => {
    if (!item) return;
    setEnabled(Boolean(item.override));
    setDraft({ ...filter, ...item.override });
  }, [item, filter]);

  const handleSave = () => {
    if (!item) return;
    setItems((items) =>
      items.map((i) =>
        i.instanceId === item.instanceId
          ? { ...i, override: enabled ? draft : undefined }
          : i
      )
    );
    onClose();
  };

  const title = item
    ? ReportComponentHelper.definition(item.componentId).title
    : '';

  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Period override — {title}</DialogTitle>
          <DialogDescription>
            By default every component follows the report period. Enable an
            override to pin this component to its own period and comparison.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-xl">
          <div className="flex items-center gap-md">
            <Switch
              id="override-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <Label htmlFor="override-enabled">
              Use a component-specific period
            </Label>
          </div>
          {enabled && (
            <PeriodControls
              idPrefix="override"
              value={draft}
              onChange={setDraft}
              months={dataset.months.map((m) => m.month)}
              booksClosedThrough={booksClosedThrough}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
